import React, { useState, useEffect, useRef, useCallback } from "react";
import { Constants, CKC_TCN_FEE_DEFAULT } from "@eagerdog/constants";
import { IEvent, EventDto, IClub, IAccountStatus, IUser, IQuery } from "@eagerdog/interfaces";
import { AxiosError } from "axios";
import { toast } from "../../../components/Toast/ToastManager";
import { Link } from "react-router-dom";

import moment from "moment";
import "moment-timezone";
import { apiService } from "../../../services/api.service";
import { requestShareToFeed } from "../../../routes/Discover/Feed/ShareToFeed/shareToFeedPrompt";

import StepForm, { Step, useStepForm } from "../StepForm";
import Input from "../../Input/Input";
import DatePick from "../../DatePick/DatePick";
import Phone from "../../Phone/Phone";
import Dropdown, { States, Provinces, IOption } from "../../Dropdown/Dropdown";

import date from "../../../assets/icons/date.svg";
import flagIcon from "../../../assets/icons/flag.svg";
import pinIcon from "../../../assets/icons/pin.svg";

import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { TINYMCE_API_KEY, TINYMCE_SETUP } from 'src/utils/tinymce';

import "./EventForm.scss";
import { helperService } from "../../../services/helper.service";
import Checkbox from "../../Checkbox/Checkbox";

interface IProps {
  onFinish(event: IEvent): void;
  user?: IUser;
  event?: IEvent;
  prePopulate?: any;
  clubNumber?: string;
  clubId?: string; // For admin users creating events for a specific club
  initialTab?: number; // Tab index to open initially
}

const EventForm: React.FC<IProps> = (props) => {
  const { activeTab, switchToTab } = useStepForm();

  const [loaded, setLoaded] = useState<boolean>(false);

  const [currentClub, setCurrentClub] = useState<IClub>();
  const [accountStatus, setAccountStatus] = useState<IAccountStatus>();

  const [steps, setSteps] = useState<string[]>(
    props.event
      ? [ "Name and Date", "Location", "Sanctioning Club", "Secretary", "Chairperson", "Message", "Waiver", "Visibility"]
      : [ "Name and Date", "Location", "Sanctioning Club", "Secretary", "Chairperson", "Message", "Waiver"]
  );

  const [clubName, setClubName] = useState<string>(
    (props.event?.club_name ?? props.user?.display_name) ?? "",
  );

  useEffect(() => {
    setClubName((props.event?.club_name ?? props.user?.display_name) ?? "");
  }, [props.event?._id, props.user?.display_name]);

  const getArmBandNumStart = useCallback(() => {
    return props.event?.arm_number_start.toString() ?? "100";
  }, [props.event?.arm_number_start]);

  const [armNum, setArmNum] = useState<string>(getArmBandNumStart());

  const [autoAssignArmbandNumbers, setAutoAssignArmbandNumbers] = useState<boolean>(
    props.event?.auto_assign_armband_numbers ?? true
  );

  const [eventName, setEventName] = useState<string>(
    props.event ? props.event.title : props.prePopulate ? props.prePopulate["eN"] : ""
  );

  const [startDate, setStartDate] = useState(
    props.event ? new Date(props.event.start_date) : props.prePopulate ? new Date(parseInt(moment(props.prePopulate["sD"]?.replace(",", "/")).format("X")) * 1000) : new Date()
  );

  const [endDate, setEndDate] = useState<Date>(() => {
    if (props.event) {
      return props.event.end_date
        ? new Date(props.event.end_date)
        : moment(props.event.start_date).add(props.event.number_of_days - 1, "days").toDate();
    }

    if (props.prePopulate?.["eD"]) {
      return new Date(parseInt(moment(props.prePopulate["eD"].replace(",", "/")).format("X")) * 1000);
    }

    if (props.prePopulate?.["eL"]) {
      return moment(startDate).add(parseInt(props.prePopulate["eL"]) - 1, "days").toDate();
    }

    return moment(startDate).add(1, "days").toDate();
  });

  const [publishOption, setPublishOption] = useState<IOption>(
    props.event && moment(props.event.publish_at).format("X") > moment(new Date()).format("X") ? { value: "On a Specific Date", id: "On a Specific Date" } : props.prePopulate ? { value: props.prePopulate["pE"], id: props.prePopulate["pE"] } : { value: "Immediately", "id": "Immediately" }
  );

  const [eventVisibility, setEventVisibility] = useState<IOption>(
    props.event?.status === Constants.event_status.draft ? { value: "Draft", id: "Draft" } : { value: "Published", id: "Published" }
  );

  const [timezone, setTimezone] = useState<IOption>(
    props.event?.timezone
      ? { id: props.event.timezone, value: helperService.getTimezoneLabel(props.event.timezone) }
      : props.prePopulate
      ? { id: props.prePopulate["tz"], value: helperService.getTimezoneLabel(props.prePopulate["tz"]) }
      : { id: "America/Toronto", value: helperService.getTimezoneLabel("America/Toronto") }
  );

  const [publishDate, setPublishDate] = useState(
    props.event?.publish_at ? props.event?.timezone ? helperService.getDateTimeWithTimezone(props.event.timezone, props.event.publish_at) : new Date(props.event.publish_at) : props.prePopulate ? new Date(parseInt(moment(props.prePopulate["pED"]?.replace(",", "/")).format("X")) * 1000) : new Date(Date.now() + 3600 * 1000 * 24)
  );

  const [clubNumber, setClubNumber] = useState<string>(
    props.event?.club?.club_number || props.clubNumber || (props.prePopulate ? props.prePopulate["clNum"] : "")
  );

  const [eventAddress, setEventAddress] = useState<string>(
    props.event?.address?.line1 ? props.event.address.line1 : props.prePopulate ? props.prePopulate["eA"] : ""
  );

  const [eventAddress2, setEventAddress2] = useState<string>(
    props.event?.address?.line2 ? props.event.address.line2 : props.prePopulate ? props.prePopulate["eA2"] : ""
  );

  const [eventCity, setEventCity] = useState<string>(
    props.event?.address?.city ? props.event.address.city : props.prePopulate ? props.prePopulate["eCi"] : ""
  );

  const [eventCountry, setEventCountry] = useState<IOption>(
    props.event?.address?.country ? { value: props.event.address.country, id: props.event.address.country }  : props.prePopulate ? { value: props.prePopulate["eCo"], id: props.prePopulate["eCo"] } : { value: "USA", id: "USA" }
  );

  const [eventProvince, setEventProvince] = useState<IOption>(
    props.event?.address?.state ? { value: props.event.address.state, id: props.event.address.state }  : props.prePopulate ? { value: props.prePopulate["eP"], id: props.prePopulate["ePid"] } : { value: "Alberta", id: "AB" }
  );

  const [eventState, setEventState] = useState<IOption>(
    props.event?.address?.state ? { value: props.event.address.state, id: props.event.address.state }  : props.prePopulate ? { value: props.prePopulate["eP"], id: props.prePopulate["ePid"] } : { value: "Alabama", id: "AL" }
  );

  const [eventZip, setEventZip] = useState<string>(
    props.event?.address?.postal_code ? props.event.address.postal_code : props.prePopulate ? props.prePopulate["pC"] : ""
  );

  const [isIndoor, setIsIndoor] = useState<boolean>(
    props.event?.conditions.includes("Indoor") ? true : props.prePopulate ? props.prePopulate["i"] === "1" ? true : false : false
  );

  const [isOutdoor, setIsOutdoor] = useState<boolean>(
    props.event?.conditions.includes("Outdoor") ? true : props.prePopulate ? props.prePopulate["o"] === "1" ? true : false : false
  );

  const [isVirtual, setIsVirtual] = useState<boolean>(
    props.event?.conditions.includes("Virtual") ? true : props.prePopulate ? props.prePopulate["v"] === "1" ? true : false : false
  );

  const [sanctionClub, setSanctionClub] = useState<IOption>(
    props.event?.sanctioning_club ? { value: props.event?.sanctioning_club, id: props.event?.sanctioning_club }  : props.prePopulate ? { value: props.prePopulate["sC"], id: "UKC" } : { value: "UKC", id: "UKC" }
  );

  const [armbandGeneration, setArmbandGeneration] = useState<IOption>(
    props.event?.armband_generation ? { value: props.event?.armband_generation, id: props.event?.armband_generation }  : props.prePopulate ? { value: props.prePopulate["arm_gen"], id: props.prePopulate["arm_gen"] } : { value: Constants.armband_generation.for_event, id: Constants.armband_generation.for_event }
  );

  const [armbandOrderType, setArmbandOrderType] = useState<IOption>(
    props.event?.armband_order_type ? { value: props.event?.armband_order_type, id: props.event?.armband_order_type } : { value: Constants.armband_order_type.registration_order, id: Constants.armband_order_type.registration_order }
  );
  const isUkcBreedGroupOrder =
    sanctionClub.value === Constants.sanctioning_club.UKC &&
    armbandOrderType.value === Constants.armband_order_type.breed_group_order;

  // Keep the end date from drifting before the start date if the start
  // date is moved forward past it.
  useEffect(() => {
    if (moment(endDate).isBefore(startDate, "day")) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  const getDefaultFeeForClub = (club: string) =>
    club === Constants.sanctioning_club.AKC ? "4.50" : club === Constants.sanctioning_club.UKC ? "4.00" : "4.00";

  const initialSanctionClub = props.event?.sanctioning_club ?? props.prePopulate?.["sC"] ?? "UKC";
  const [sanctioningFee, setSanctioningFee] = useState<string>(
    props.event?.sanctioning_fee !== undefined ? props.event.sanctioning_fee.toFixed(2) : getDefaultFeeForClub(initialSanctionClub)
  );
  // CKC: Temporary Competition Number listing fee, charged per dog per show (tax included)
  const [tcnFee, setTcnFee] = useState<string>(
    props.event?.tcn_fee !== undefined ? props.event.tcn_fee.toFixed(2) : CKC_TCN_FEE_DEFAULT.toFixed(2)
  );

  const editSecretary = props.event?.contacts.filter(
    (c) => c.contact_type === "secretary"
  )[0];

  const [secFirst, setSecFirst] = useState<string>(
    editSecretary ? editSecretary.first_name : props.prePopulate ? props.prePopulate["secF"] : ""
  );
  const [secLast, setSecLast] = useState<string>(
    editSecretary ? editSecretary.last_name : props.prePopulate ? props.prePopulate["secL"] : ""
  );
  const [secPhone, setSecPhone] = useState<string>(
    editSecretary ? editSecretary.phone : props.prePopulate ? props.prePopulate["secP"] : ""
  );
  const [secEmail, setSecEmail] = useState<string>(
    editSecretary ? editSecretary.email : props.prePopulate ? props.prePopulate["secE"] : ""
  );

  const [secretaryAddress, setSecretaryAddress] = useState<string>(
    helperService.getStringValue(editSecretary?.address?.line1, props.prePopulate, "secA")
  );

  const [secretaryAddress2, setSecretaryAddress2] = useState<string>(
    helperService.getStringValue(editSecretary?.address?.line2, props.prePopulate, "secA2")
  );

  const [secretaryCity, setSecretaryCity] = useState<string>(
    helperService.getStringValue(editSecretary?.address?.city, props.prePopulate, "secCi")
  );

  const [secretaryZip, setSecretaryZip] = useState<string>(
    helperService.getStringValue(editSecretary?.address?.postal_code, props.prePopulate, "secZip")
  );

  const [secretaryCountry, setSecretaryCountry] = useState<IOption>(
    helperService.getOptionValue(
      editSecretary?.address?.country,
      props.prePopulate,
      { value: "USA", id: "USA" },
      "secCo"
    )
  );

  const [secretaryProvince, setSecretaryProvince] = useState<IOption>(
    helperService.getOptionValue(
      editSecretary?.address?.state,
      props.prePopulate,
      { value: "Alberta", id: "AB" },
      "secPr",
      "secPid"
    )
  );

  const [secretaryState, setSecretaryState] = useState<IOption>(
    helperService.getOptionValue(
      editSecretary?.address?.state,
      props.prePopulate,
      { value: "Alabama", id: "AL" },
      "secPr",
      "secPid"
    )
  );
  const [secretaryMode, setSecretaryMode] = useState<"trusted" | "manual">("manual");
  const [trustedSecretarySearch, setTrustedSecretarySearch] = useState<string>("");
  const [trustedSecretaryResults, setTrustedSecretaryResults] = useState<IUser[]>([]);
  const [isSearchingTrustedSecretary, setIsSearchingTrustedSecretary] = useState<boolean>(false);
  const [selectedTrustedSecretary, setSelectedTrustedSecretary] = useState<IUser | null>(null);

  const editChair = props.event?.contacts.filter(
    (c) => c.contact_type === "chairperson"
  )[0];

  const [chairFirst, setChairFirst] = useState<string>(
    editChair ? editChair.first_name : props.prePopulate ? props.prePopulate["chaF"] : ""
  );
  const [chairLast, setChairLast] = useState<string>(
    editChair ? editChair.last_name : props.prePopulate ? props.prePopulate["chaL"] : ""
  );
  const [chairPhone, setChairPhone] = useState<string>(
    editChair ? editChair.phone : props.prePopulate ? props.prePopulate["chaP"] : ""
  );
  const [chairEmail, setChairEmail] = useState<string>(
    editChair ? editChair.email : props.prePopulate ? props.prePopulate["chaE"] : ""
  );

  const [chairpersonAddress, setChairpersonAddress] = useState<string>(
    helperService.getStringValue(editChair?.address?.line1, props.prePopulate, "chaA")
  );

  const [chairpersonAddress2, setChairpersonAddress2] = useState<string>(
    helperService.getStringValue(editChair?.address?.line2, props.prePopulate, "chaA2")
  );

  const [chairpersonCity, setChairpersonCity] = useState<string>(
    helperService.getStringValue(editChair?.address?.city, props.prePopulate, "chaCi")
  );

  const [chairpersonZip, setChairpersonZip] = useState<string>(
    helperService.getStringValue(editChair?.address?.postal_code, props.prePopulate, "chaZip")
  );

  const [chairpersonCountry, setChairpersonCountry] = useState<IOption>(
    helperService.getOptionValue(
      editChair?.address?.country,
      props.prePopulate,
      { value: "USA", id: "USA" },
      "chaCo"
    )
  );

  const [chairpersonProvince, setChairpersonProvince] = useState<IOption>(
    helperService.getOptionValue(
      editChair?.address?.state,
      props.prePopulate,
      { value: "Alberta", id: "AB" },
      "chaPr",
      "chaPid"
    )
  );

  const [chairpersonState, setChairpersonState] = useState<IOption>(
    helperService.getOptionValue(
      editChair?.address?.state,
      props.prePopulate,
      { value: "Alabama", id: "AL" },
      "chaPr",
      "chaPid"
    )
  );

  const [stripeLink, setStripeLink] = useState<string>("");

  const [isLottery, setIsLottery] = useState<boolean>(
    props.prePopulate ? props.prePopulate["isLot"] === "1" : props.event?.is_lottery ?? false
  );

  const [lotteryEndDate, setLotteryEndDate] = useState(
    props.event?.lottery_end_date ? new Date(props.event.lottery_end_date) : props.prePopulate ? new Date(parseInt(moment(props.prePopulate["lotEnd"]?.replace(",", "/")).format("X")) * 1000) : new Date(Date.now() + 3600 * 1000 * 24)
  );

  const [lotteryRunDate, setLotteryRunDate] = useState(
    props.event?.lottery_run_date ? new Date(props.event.lottery_run_date) : new Date(Date.now() + 3600 * 1000 * 24)
  );

  // NOTE: PWDCA specific info
  const [arbitrationCity, setArbitrationCity] = useState<string>(
    props.event?.arbitration_address?.city ? props.event.arbitration_address.city : props.prePopulate ? props.prePopulate["arCity"] : ""
  );

  const [arbitrationCountry, setArbitrationCountry] = useState<IOption>(
    props.event?.arbitration_address?.country ? { value: props.event.arbitration_address.country, id: props.event.arbitration_address.country } : props.prePopulate ? { value: props.prePopulate["arCountry"], id: props.prePopulate["arCountry"] } : eventCountry
  );
  const [arbitrationProvince, setArbitrationProvince] = useState<IOption>(
    props.event?.arbitration_address?.state ? { value: props.event.arbitration_address.state, id: props.event.arbitration_address.state } : props.prePopulate ? { value: props.prePopulate["arProvince"], id: props.prePopulate["arProvince"] } : eventProvince
  );
  const [arbitrationState, setArbitrationState] = useState<IOption>(
    props.event?.arbitration_address?.state ? { value: props.event.arbitration_address.state, id: props.event.arbitration_address.state } : props.prePopulate ? { value: props.prePopulate["arState"], id: props.prePopulate["arState"] } : eventState
  );

  const sanctionClubs: IOption[] = [
    { value: "ABI", id: "ABI" },
    { value: "AHBA", id: "AHBA" },
    { value: "AKC", id: "AKC" },
    { value: "ASFA", id: "ASFA" },
    { value: "BHA", id: "BHA" },
    { value: "CDSP", id: "CDSP" },
    { value: "CKC", id: "CKC" },
    { value: "CPE", id: "CPE" },
    { value: "C-Wags", id: "C-Wags" },
    { value: "4-H", id: "4-H" },
    { value: "GCR", id: "GCR" },
    { value: "General", id: "General" },
    { value: "ISWS", id: "ISWS" },
    { value: "PWDCA", id: "PWDCA" },
    { value: "UKC", id: "UKC" },
    
  ];

  const armbandGenerationOptions: IOption[] = [
    { value: Constants.armband_generation.for_event, id: Constants.armband_generation.for_event },
    { value: Constants.armband_generation.for_show, id: Constants.armband_generation.for_show },
  ];

  const armbandOrderTypeOptions: IOption[] = [
    { value: Constants.armband_order_type.registration_order, id: Constants.armband_order_type.registration_order },
    { value: Constants.armband_order_type.breed_group_order, id: Constants.armband_order_type.breed_group_order },
  ];

  const [ isCreatingEvent, setIsCreatingEvent ] = useState<boolean>(false);

  const editorRef = useRef<TinyMCEEditor | null>(null);
  const eventWaiverEditorRef = useRef<TinyMCEEditor | null>(null);

  const registrationMessage = props.event?.registration_message ? props.event.registration_message : props.prePopulate ? props.prePopulate["rm"] : "";

  const waiver = props.event?.waiver ? props.event.waiver : props.prePopulate ? props.prePopulate["waiver"] : "";


  useEffect(() => {
    if (!loaded) {
      // If clubId is provided (admin creating event for specific club), load that club
      // If editing an event, use the event's club_id
      // Otherwise, load the current user's club
      const clubPromise = props.clubId 
        ? apiService.getClub(props.clubId)
        : props.event?.club_id
        ? apiService.getClub(props.event.club_id)
        : apiService.getCurrentClub();

      clubPromise.then((club) => {
        if (club._id) {
          setCurrentClub(club);
          
          // Set the club name as default for the event title
          if (props.clubId && !props.event) {
            setClubName(club.club_name || "");
          }

          apiService.getAccountStatus(club._id).then((status) => {
            setAccountStatus(status);

            if (status.onboarding_status !== "requirements_met" ||props.prePopulate) {
              let _steps: string[] = [...steps];
              _steps.push("Stripe");

              setSteps(_steps);

              if (props.prePopulate) {
                switchToTab(7);
                window.history.pushState("", "", window.location.href.split("?")[0]);
              }
            }
          });
        }
      });

      setLoaded(true);

      // Switch to initial tab if specified
      if (props.initialTab !== undefined) {
        setTimeout(() => {
          switchToTab(props.initialTab!);
        }, 100);
      }
    }
  }, [loaded, steps, props.prePopulate, switchToTab, props.clubId, props.event, props.initialTab]);

  useEffect(() => {
    if (editSecretary?.is_trusted_secretary) {
      const secretaryUser = {
        _id: editSecretary.user_id || "",
        first_name: editSecretary.first_name,
        last_name: editSecretary.last_name,
        email: editSecretary.email,
        phone: editSecretary.phone,
        display_name: `${editSecretary.first_name} ${editSecretary.last_name}`.trim(),
      } as IUser;
      setSelectedTrustedSecretary(secretaryUser);
      setTrustedSecretarySearch(editSecretary.email || secretaryUser.display_name);
    }
  }, [editSecretary]);

  // Update default per-entry fee when sanctioning club changes (new events only)
  useEffect(() => {
    if (!props.event && (sanctionClub.value === Constants.sanctioning_club.AKC || sanctionClub.value === Constants.sanctioning_club.UKC)) {
      setSanctioningFee(sanctionClub.value === Constants.sanctioning_club.AKC ? "4.50" : "4.00");
    }
  }, [sanctionClub.value, props.event]);

  // UKC Breed Group Order must use Per Entire Event during event creation.
  useEffect(() => {
    if (
      !props.event &&
      isUkcBreedGroupOrder &&
      armbandGeneration.value !== Constants.armband_generation.for_event
    ) {
      setArmbandGeneration({
        value: Constants.armband_generation.for_event,
        id: Constants.armband_generation.for_event,
      });
    }
  }, [props.event, isUkcBreedGroupOrder, armbandGeneration.value]);

  const getRegionOption = (countryValue: string, regionCode?: string): IOption => {
    if (countryValue === "Canada") {
      return (
        Provinces.find((o) => o.id === regionCode) ||
        Provinces.find((o) => o.value === regionCode) ||
        { value: "Alberta", id: "AB" }
      );
    }

    return (
      States.find((o) => o.id === regionCode) ||
      States.find((o) => o.value === regionCode) ||
      { value: "Alabama", id: "AL" }
    );
  };

  const applyTrustedSecretary = (trustedUser: IUser) => {
    setSecFirst(trustedUser.first_name || "");
    setSecLast(trustedUser.last_name || "");
    setSecPhone(trustedUser.phone || "");
    setSecEmail(trustedUser.email || "");

    const userCountry = trustedUser.address?.country || "USA";
    const countryOption = { value: userCountry, id: userCountry };
    setSecretaryCountry(countryOption);
    setSecretaryAddress(trustedUser.address?.line1 || "");
    setSecretaryAddress2(trustedUser.address?.line2 || "");
    setSecretaryCity(trustedUser.address?.city || "");
    setSecretaryZip(trustedUser.address?.postal_code || "");

    const regionOption = getRegionOption(userCountry, trustedUser.address?.state);
    if (userCountry === "Canada") {
      setSecretaryProvince(regionOption);
    } else {
      setSecretaryState(regionOption);
    }
  };

  const searchTrustedSecretaries = async (searchText: string) => {
    if (!searchText.trim()) {
      setTrustedSecretaryResults([]);
      return;
    }

    setIsSearchingTrustedSecretary(true);
    const query: IQuery = {
      $limit: 8,
      sort: [{ attribute_name: "display_name", sort: "asc" }],
      query: {
        $and: [
          {
            attribute_name: "user_type",
            attribute_value: { operator: "$eq", value: Constants.user_type.owner },
          },
          {
            attribute_name: "is_trusted_secretary",
            attribute_value: { operator: "$eq", value: true },
          },
          {
            $or: [
              {
                attribute_name: "display_name",
                attribute_value: { operator: "$regex", value: searchText },
              },
              {
                attribute_name: "first_name",
                attribute_value: { operator: "$regex", value: searchText },
              },
              {
                attribute_name: "last_name",
                attribute_value: { operator: "$regex", value: searchText },
              },
              {
                attribute_name: "email",
                attribute_value: { operator: "$regex", value: searchText },
              },
            ],
          },
        ],
      },
    };

    try {
      const users = await apiService.getUsers(query);
      setTrustedSecretaryResults(users || []);
    } catch (_e) {
      setTrustedSecretaryResults([]);
    } finally {
      setIsSearchingTrustedSecretary(false);
    }
  };

  useEffect(() => {
    if (secretaryMode !== "trusted") {
      setTrustedSecretaryResults([]);
      setIsSearchingTrustedSecretary(false);
      return;
    }

    const keyword = trustedSecretarySearch.trim();
    if (!keyword || selectedTrustedSecretary?.email === trustedSecretarySearch) {
      setTrustedSecretaryResults([]);
      return;
    }

    const timeout = window.setTimeout(() => {
      searchTrustedSecretaries(keyword);
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [secretaryMode, trustedSecretarySearch, selectedTrustedSecretary]);

  const getCondition = () => {
    let condition: string = "";

    if (isVirtual) {
      condition = "Virtual";
    } else if (isIndoor && isOutdoor) {
      condition = "Indoor & Outdoor";
    } else if (isIndoor) {
      condition = "Indoor";
    } else if (isOutdoor) {
      condition = "Outdoor";
    }

    return condition;
  };

  const validateEvent = () => {
    let passed: string = "";

    if (eventName === "") {
      passed = "Please set a name for your event";
      switchToTab(0);
    }

    const eventDays = moment(endDate).startOf("day").diff(moment(startDate).startOf("day"), "days") + 1;
    if (eventDays < 1 || eventDays > 90) {
      passed = "Event length must be between 1 and 90 days";
      switchToTab(0);
    }

    const feeValue = parseFloat(sanctioningFee);
    if ((sanctionClub.value === Constants.sanctioning_club.AKC || sanctionClub.value === Constants.sanctioning_club.UKC) && 
        (isNaN(feeValue) || feeValue <= 0 || feeValue >= 10)) {
      passed = "Sanctioning fee must be between $0.01 and $9.99";
      switchToTab(2);
    }

    if (eventAddress === "") {
      passed = "Please set an address for your event";
      switchToTab(1);
    }

    if (eventZip === "") {
      passed = "Please set a postal/zip code for your event";
      switchToTab(1);
    }

    if (
      (eventCountry.value === "Canada" && eventProvince.value === "") ||
      (eventCountry.value !== "Canada" && eventState.value === "")
    ) {
      passed = "Please select a province/state for your event";
      switchToTab(1);
    }

    if (eventCity === "") {
      passed = "Please set a city for your event";
      switchToTab(1);
    }

    if (getCondition() === "") {
      passed = "Please select Indoor, Outdoor, or both for your event";
      switchToTab(1);
    }

    if (secFirst === "" || secLast === "") {
      passed = "Please enter a first and last name for the event secretary";
      switchToTab(3);
    }

    if (!secPhone || secPhone.trim() === "") {
      passed = "Please enter a phone number for the event secretary";
      switchToTab(3);
    }

    if (chairFirst === "" || chairLast === "") {
      passed = "Please enter a first and last name for the event chairperson";
      switchToTab(4);
    }

    if (!chairPhone || chairPhone.trim() === "") {
      passed = "Please enter a phone number for the event chairperson";
      switchToTab(4);
    }

    if (
      accountStatus?.onboarding_status === "past_due" ||
      accountStatus?.onboarding_status === "currently_due"
    ) {
      passed =
        "Your stripe account is missing important details, please add stripe details before creating an event";
      switchToTab(7);
    }

    if (sanctionClub.value === Constants.sanctioning_club.UKC && !clubNumber) {
      passed =
        "Please enter a club number";
      switchToTab(2);
    }

    return passed;
  };

  const createEvent = () => {
    let _error: string = validateEvent();

    if (_error.length > 0) {
      toast.show({
        title: "Event Error",
        content: _error,
        duration: 10000,
        type: "fail",
      });

      return false;
    }

    setIsCreatingEvent(true);

    let publishAt
    if (props.event) {
      if (publishOption.value !== "Immediately" && timezone.id) {
        publishAt = moment.tz(moment(publishDate).format('YYYY-MM-DD HH:mm'), timezone.id).toISOString()
      } else {
        publishAt = new Date()
      }
    }

    // Normalize start_date to midnight in the event's timezone to avoid timezone conversion issues
    const normalizedStartDate = moment.tz(moment(startDate).format('YYYY-MM-DD'), timezone.id).toDate();

    // The event length is set via a start/end date range, so derive
    // number_of_days from that range for the backend. Diff the plain
    // calendar-date strings the user actually selected (not Date objects)
    // so this can't drift by a day when the event's timezone differs from
    // the browser's local timezone — e.g. normalizedStartDate is an absolute
    // instant that, when re-interpreted back into the browser's local time
    // via moment(...).startOf("day"), can land on the previous/next calendar
    // day and silently inflate a 1-day event into a 2-day event.
    const numberOfDays = moment(moment(endDate).format('YYYY-MM-DD'))
      .diff(moment(moment(startDate).format('YYYY-MM-DD')), "days") + 1;

    let payload: any = {
      premium_file_url: props.event?.premium_file_url,
      flyer_image_url: props.event?.flyer_image_url,
      club_name: clubName,
      title: eventName,
      sanctioning_club: sanctionClub.value,
      conditions: getCondition(),
      start_date: normalizedStartDate,
      timezone: timezone.id,
      arm_number_start: armNum,
      auto_assign_armband_numbers: autoAssignArmbandNumbers,
      armband_generation: sanctionClub.value === Constants.sanctioning_club.UKC
        ? (isUkcBreedGroupOrder
          ? Constants.armband_generation.for_event
          : armbandGeneration.value)
        : undefined,
      armband_order_type: sanctionClub.value === Constants.sanctioning_club.UKC ? armbandOrderType.value : undefined,
      sanctioning_fee: parseFloat(sanctioningFee) || (sanctionClub.value === Constants.sanctioning_club.AKC ? 4.50 : 4.00),
      tcn_fee: sanctionClub.value === Constants.sanctioning_club.CKC
        ? (tcnFee === "" ? 0 : parseFloat(tcnFee))
        : undefined,
      number_of_days: numberOfDays,
      publish_at: props.event ? publishAt : undefined,
      status: props.event 
        ? eventVisibility.value === "Draft" 
          ? Constants.event_status.draft 
          : publishOption.value === "Immediately" 
            ? Constants.event_status.published 
            : Constants.event_status.upcoming
        : Constants.event_status.draft,
      address: {
        line1: eventAddress,
        line2: eventAddress2,
        city: eventCity,
        state: eventCountry.value === "Canada" ? eventProvince.id : eventState.id,
        country: eventCountry.value,
        postal_code: eventZip,
      },
      contacts: [
        {
          contact_type: "secretary",
          first_name: secFirst,
          last_name: secLast,
          phone: secPhone,
          email: secEmail,
          user_id:
            secretaryMode === "trusted" && selectedTrustedSecretary?._id
              ? selectedTrustedSecretary._id
              : undefined,
          is_trusted_secretary:
            secretaryMode === "trusted" && !!selectedTrustedSecretary,
          address: helperService.canEditAddressForContacts(sanctionClub.value) ? {
            line1: secretaryAddress,
            line2: secretaryAddress2,
            city: secretaryCity,
            state: secretaryCountry.value === "Canada" ? secretaryProvince.id : secretaryState.id,
            country: secretaryCountry.value,
            postal_code: secretaryZip,
          } : {}
        },
        {
          contact_type: "chairperson",
          first_name: chairFirst,
          last_name: chairLast,
          phone: chairPhone,
          email: chairEmail,
          address: helperService.canEditAddressForContacts(sanctionClub.value) ? {
            line1: chairpersonAddress,
            line2: chairpersonAddress2,
            city: chairpersonCity,
            state: chairpersonCountry.value === "Canada" ? chairpersonProvince.id : chairpersonState.id,
            country: chairpersonCountry.value,
            postal_code: chairpersonZip,
          } : {}
        },
      ],
      registration_message: editorRef.current ? editorRef.current.getContent() : '',
      waiver: eventWaiverEditorRef.current ? eventWaiverEditorRef.current.getContent() : '',
      //show_types: []
    };

    // Add club_id for admin users
    if (props.clubId) {
      payload.club_id = props.clubId;
    }

    if (clubNumber) {
      payload.club_number = clubNumber;
    }

    if ([Constants.sanctioning_club.PWDCA, Constants.sanctioning_club.AKC].includes(sanctionClub.value)) {
      payload = {
        ...payload,
        is_lottery: isLottery,
        lottery_run_date: lotteryRunDate,
        lottery_end_date: lotteryEndDate,
      };
    }

    if (sanctionClub.value === Constants.sanctioning_club.PWDCA ) {
      payload = {
        ...payload,
        arbitration_address: {
          city: arbitrationCity,
          country: arbitrationCountry.value,
          state: arbitrationCountry.value === "Canada" ? arbitrationProvince.id : arbitrationState.id,
        },
      };
    }

    if (props.event) {
      const wasDraft = props.event.status === Constants.event_status.draft;
      apiService.updateEvent(props.event._id, payload as EventDto).then((response) => {
        if (response._id) {
          props.onFinish(response);
          // First time public: offer to customize the feed post.
          if (wasDraft && response.status !== Constants.event_status.draft) {
            requestShareToFeed(response);
          }
        }
      }).catch((e: AxiosError) => {
        // Extract specific validation error message if available
        const errorMessage = (e.response?.data as any)?.message || "Unable to update event";
        toast.show({
          title: "Update Event",
          content: errorMessage,
          duration: 10000,
          errorDetails: e,
          type: "fail",
        });
      }).finally(() => {
        setIsCreatingEvent(false);
      });
    } else {
      apiService.createEvent(payload as EventDto).then((response) => {
        if (response._id) {
          props.onFinish(response);
        }
      }).catch((e: AxiosError) => {
        // Extract specific validation error message if available
        const errorMessage = (e.response?.data as any)?.message || "Unable to create event";
        toast.show({
          title: "Create Event",
          content: errorMessage,
          duration: 10000,
          errorDetails: e,
          type: "fail",
        });
      }).finally(() => {
        setIsCreatingEvent(false);
      });
    }
  };

  const createStripeLink = () => {
    if (currentClub) {
      let _returnUrl: string = createReturnUrl();

      apiService
        .getAccountOnboardingLink(currentClub._id, {
          return_url: _returnUrl,
          refresh_url: window.location.origin + "/",
        })
        .then((response) => {
          if (response.url) {
            setStripeLink(response.url);
          }
        });
    }
  };

  const createReturnUrl = () => {
    let _url: string = window.location.origin + "/?" +
      "stripe=1&" +
      "eN=" + eventName + "&" +
      "eD=" + moment(endDate).format("M,DD,YYYY") + "&" +
      "eA=" + eventAddress + "&" +
      "eA2=" + eventAddress2 + "&" +
      "clNum=" + clubNumber + "&" +
      "eCo=" + eventCountry.value + "&" +
      "eP=" + (eventCountry.value === "Canada" ? eventProvince.value : eventState.value) + "&" +
      "ePid=" + (eventCountry.value === "Canada" ? eventProvince.id : eventState.id) + "&" +
      "eCi=" + eventCity + "&" +
      "tz=" + timezone.id + "&" +
      "i=" + (isIndoor ? "1" : "0") + "&" +
      "o=" + (isOutdoor ? "1" : "0") + "&" +
      "v=" + (isVirtual ? "1" : "0") + "&" +
      "sC=" + sanctionClub.value + "&" +
      "arm_gen=" + armbandGeneration.value + "&" +
      "secF=" + secFirst + "&" +
      "secL=" + secLast + "&" +
      "secP=" + secPhone + "&" +
      "secE=" + secEmail + "&" +
      "chaF=" + chairFirst + "&" +
      "chaL=" + chairLast + "&" +
      "chaP=" + chairPhone + "&" +
      "chaE=" + chairEmail + "&" +
      "pC=" + eventZip + "&" +
      "sD=" + moment(startDate).format("M,DD,YYYY") + "&" +
      "pE=" + publishOption.value + "&" +
      "pED=" + moment(publishDate).format("M,DD,YYYY") + "&" +
      "rm=" + registrationMessage + "&" + //editorRef.current?.getContent();
      "waiver=" + waiver  + "&" +
      "arCity=" + arbitrationCity + "&" +
      "arCountry=" + arbitrationCountry.value + "&" +
      "arState=" + arbitrationState.value + "&" +
      "arProvince=" + arbitrationProvince.value;

    if (helperService.canEditAddressForContacts(sanctionClub.value)) {
      _url += "secE=" + secEmail + "&" +
        "secA=" + secretaryAddress + "&" +
        "secA2=" + secretaryAddress2 + "&" +
        "secCo=" + secretaryCountry.value + "&" +
        "secPr=" + (secretaryCountry.value === "Canada" ? secretaryProvince.value : secretaryState.value) + "&" +
        "secPid=" + (secretaryCountry.value === "Canada" ? secretaryProvince.id : secretaryState.id) + "&" +
        "secCi=" + secretaryCity + "&" +
        "secZip=" + secretaryZip + "&" +
        "chaA=" + chairpersonAddress + "&" +
        "chaA2=" + chairpersonAddress2 + "&" +
        "chaCo=" + chairpersonCountry.value + "&" +
        "chaPr=" + (chairpersonCountry.value === "Canada" ? chairpersonProvince.value : chairpersonState.value) + "&" +
        "chaPid=" + (chairpersonCountry.value === "Canada" ? chairpersonProvince.id : chairpersonState.id) + "&" +
        "chaCi=" + chairpersonCity + "&" +
        "chaZip=" + chairpersonZip + "&"
    }

    if ([Constants.sanctioning_club.PWDCA, Constants.sanctioning_club.AKC].includes(sanctionClub.value)) {
      _url += "lotEnd=" + moment(lotteryEndDate).format("M,DD,YYYY") + "&" +
      "isLot=" + (isLottery ? "1" : "0");
    }

    return encodeURI(_url);
  };



  return (
    <div className="EventForm">
      <div className="title">
        {props.event ? "Edit Event" : "Create an Event"}
      </div>
      <div className="formWrap">
        <StepForm activeTab={activeTab} next={(e) => {   switchToTab(e); }} onSubmit={createEvent} steps={steps} finishText={props.event ? "Update" : "Finish"} disabled={isCreatingEvent} isLoading={isCreatingEvent}>
          <Step>
            <div className="stepTitle">Name and Date</div>
            <p>What's the name and date of your event?</p>
            <Input required label="Club Name" defaultValue={clubName} id="clubName" type="text" onChange={(e) => { e.stopPropagation(); setClubName(e.target.value); }} placeholder="Club name" />
            <Input required label="Event Name" defaultValue={props.event ? props.event.title : eventName} id="eventName" type="text" onChange={(e) => { e.stopPropagation(); setEventName(e.target.value); }} placeholder="What's your event's name?" />
            <DatePick label="Event Start Date" onChange={setStartDate} value={startDate} />
            <DatePick label="Event End Date" minDate={startDate} value={endDate} onChange={(newDate: Date) => { setEndDate(newDate); }} />
            <Dropdown value={timezone} onChange={(e: any, value: IOption) => { setTimezone(value); }} icon={date} label="Timezone" options={helperService.usCanadaTimezoneOptions} placeholder="Choose a timezone" />
          </Step>
          <Step>
            <div className="stepTitle">Location</div>
            <p>Where is your event located?</p>
            <div className="half">
              <Input required label="Address Line 1 or Name of Location" id="eventAddress" defaultValue={eventAddress} type="text" onChange={(e) => { e.stopPropagation(); setEventAddress(e.target.value); }} placeholder="What's your event's address?" />
              <Input label="Address Line 2" id="eventAddress2" type="text" defaultValue={eventAddress2} onChange={(e) => { e.stopPropagation(); setEventAddress2(e.target.value); }} placeholder=""/>
            </div>
            <div className="half">
              <Input required label="City" id="eventCity" defaultValue={eventCity} type="text" onChange={(e) => { e.stopPropagation(); setEventCity(e.target.value); }} />
              <Input required label="Postal/ZIP Code" id="eventZip" defaultValue={eventZip} type="text" onChange={(e) => { e.stopPropagation(); setEventZip(e.target.value); }} />
            </div>
            <div className="half">
              <Dropdown value={eventCountry} onChange={(e: any, value: IOption) => { setEventCountry(value); }} icon={flagIcon} label="Country" options={[ { value: "Canada", id: "Canada" }, { value: "USA", id: "USA" } ]} placeholder="Choose your country of residence" />
              <>
                {eventCountry.id === "USA" && (<Dropdown value={eventState} onChange={(e: any, value: IOption) => { setEventState(value); }} icon={pinIcon} label="State" options={States} placeholder="Choose your state" />)}
              </>
              <>
                {eventCountry.id === "Canada" && (<Dropdown value={eventProvince} onChange={(e: any, value: IOption) => { setEventProvince(value); }} icon={pinIcon} label="Province / State" options={Provinces} alignRight={true} placeholder="Choose your province" />)}
              </>
            </div>
            <p>Please select all that apply.</p>
            <div className="checkWrap half">
              <div className="checkboxWrap">
                <label className="checkboxLabel" htmlFor="isIndoor">
                  <div className="checkmarkWrap">
                    <input type="checkbox" id="isIndoor" name="isIndoor" checked={isIndoor} onChange={(e) => { 
                      setIsIndoor(!isIndoor); 
                      if (!isIndoor) setIsVirtual(false); // Uncheck Virtual if Indoor is checked
                    }} />
                    <span className="checkmark"></span>
                  </div>
                  <span className="name">Indoor</span>
                </label>
              </div>
              <div className="checkboxWrap">
                <label className="checkboxLabel" htmlFor="isOutdoor">
                  <div className="checkmarkWrap">
                    <input type="checkbox" id="isOutdoor" name="isOutdoor" checked={isOutdoor} onChange={(e) => { 
                      setIsOutdoor(!isOutdoor); 
                      if (!isOutdoor) setIsVirtual(false); // Uncheck Virtual if Outdoor is checked
                    }} />
                    <span className="checkmark"></span>
                  </div>
                  <span className="name">Outdoor</span>
                </label>
              </div>
              <div className="checkboxWrap">
                <label className="checkboxLabel" htmlFor="isVirtual">
                  <div className="checkmarkWrap">
                    <input type="checkbox" id="isVirtual" name="isVirtual" checked={isVirtual} onChange={(e) => { 
                      setIsVirtual(!isVirtual); 
                      if (!isVirtual) { 
                        setIsIndoor(false); // Uncheck Indoor if Virtual is checked
                        setIsOutdoor(false); // Uncheck Outdoor if Virtual is checked
                      }
                    }} />
                    <span className="checkmark"></span>
                  </div>
                  <span className="name">Virtual</span>
                </label>
              </div>
            </div>
          </Step>
          <Step>
            <div className="stepTitle">Sanctioning Club</div>
            <p>
              What club is your event being run under? This can't be edited
              after the event is created.
            </p>
            <Dropdown disabled={!!props.event} value={sanctionClub} onChange={(e: any, value: IOption) => { setSanctionClub(value); }} icon={flagIcon} label="Sanctioning Club" options={sanctionClubs} placeholder="Choose a sanctioning club" />
            {(sanctionClub.value === Constants.sanctioning_club.AKC || sanctionClub.value === Constants.sanctioning_club.UKC) ? (
              <>
                <Input 
                  required 
                  onChange={(e) => { 
                    const value = e.target.value;
                    // Allow only valid currency format
                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                      setSanctioningFee(value);
                    }
                  }} 
                  type="text" 
                  label={`${sanctionClub.value} per Entry Fee`}
                  placeholder={sanctionClub.value === Constants.sanctioning_club.AKC ? "4.50" : "4.00"} 
                  value={sanctioningFee}
                />
                <div className="sanctioningFeeNote">
                  This fee amount is ONLY used in Trial reports to determine the amount your club owes to the sanctioning club. It does NOT impact your event pricing.
                </div>
              </>
            ) : <></>}
            {sanctionClub.value === Constants.sanctioning_club.CKC ? (
              <>
                <Input
                  required
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow only valid currency format
                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                      setTcnFee(value);
                    }
                  }}
                  type="text"
                  label="CKC TCN Listing Fee (per dog, per show)"
                  placeholder={CKC_TCN_FEE_DEFAULT.toFixed(2)}
                  value={tcnFee}
                />
                <div className="sanctioningFeeNote">
                  Charged to exhibitors entering a dog on a Temporary Competition Number, once per show, in addition to the entry fee. Enter the amount with tax included.
                </div>
              </>
            ) : <></>}
            {sanctionClub.value === Constants.sanctioning_club.UKC ? (
              <div>
                <Dropdown 
                  value={armbandOrderType} 
                  onChange={(e: any, value: IOption) => { 
                    setArmbandOrderType(value); 
                    // If breed group order is selected, disable auto-assign
                    if (value.value === Constants.armband_order_type.breed_group_order) {
                      setAutoAssignArmbandNumbers(false);
                      setArmbandGeneration({
                        value: Constants.armband_generation.for_event,
                        id: Constants.armband_generation.for_event,
                      });
                    }
                  }} 
                  label="Armband Type" 
                  options={armbandOrderTypeOptions} 
                  placeholder="Choose armband ordering type"
                  disabled={!!props.event}
                />
                {props.event && (
                  <p className="armbandTypeWarning">
                    Armband Type cannot be changed after event creation
                  </p>
                )}
                <Dropdown
                  value={armbandGeneration}
                  onChange={(e: any, value: IOption) => { setArmbandGeneration(value); }}
                  label="Armband Generation"
                  options={armbandGenerationOptions}
                  placeholder="Choose a armband generation option"
                  disabled={!props.event && isUkcBreedGroupOrder}
                />
                {armbandOrderType.value === Constants.armband_order_type.registration_order && (
                  <div className="Checkbox">
                    <Checkbox 
                      onChange={(e: any) => { setAutoAssignArmbandNumbers(!autoAssignArmbandNumbers); }} 
                      value={autoAssignArmbandNumbers} 
                      id="autoAssignArmbandNumbers" 
                      label="Auto assign armband numbers" 
                    />
                  </div>
                )}
                {armbandGeneration.value === Constants.armband_generation.for_event && !isUkcBreedGroupOrder && (
                  <Input className="armBandStart" required onChange={(e) => { setArmNum(e.target.value); }} type="number" label="Armband Starting Number" placeholder="Enter an armband starting number." defaultValue={armNum} />
                )}
                {!props.clubNumber && (
                  <Input required label="Club Number" id="clubNumber" defaultValue={clubNumber} type="text" onChange={(e) => { e.stopPropagation(); setClubNumber(e.target.value); }} placeholder="What's your club's number?" />
                )}
              </div>
            ) : <></>}
            {sanctionClub.value === Constants.sanctioning_club.PWDCA ? (
              <>
              <Input required label="Arbitration City" id="arbitrationCity" defaultValue={arbitrationCity} type="text"
                     onChange={(e) => {
                       e.stopPropagation();
                       setArbitrationCity(e.target.value);
                     }}/>
              <div className="half">
                <Dropdown value={arbitrationCountry} onChange={(_e: any, value: IOption) => {
                  setArbitrationCountry(value);
                }} icon={flagIcon} label="Arbitration Country"
                          options={[{ value: "Canada", id: "Canada" }, { value: "USA", id: "USA" }]}
                          placeholder="Choose a country"/>
                <>
                  {arbitrationCountry.id === "USA" && (<Dropdown value={arbitrationState} onChange={(_e: any, value: IOption) => {
                    setArbitrationState(value);
                  }} icon={pinIcon} label="Arbitration State" options={States} placeholder="Choose your state"/>)}
                </>
                <>
                  {arbitrationCountry.id === "Canada" && (
                    <Dropdown value={arbitrationProvince} onChange={(_e: any, value: IOption) => {
                      setArbitrationProvince(value);
                    }} icon={pinIcon} label="Arbitration Province / State" options={Provinces} alignRight={true}
                              placeholder="Choose a province"/>)}
                </>
              </div>
              </>
            ) : <></>}
            {[Constants.sanctioning_club.PWDCA, Constants.sanctioning_club.AKC].includes(sanctionClub.value) ? (
              <>
                <p>Will there be a random draw at this event? </p>
                <div className="Checkbox">
                  <Checkbox onChange={(e:any) => { setIsLottery(!isLottery); }} value={isLottery} id="isLottery" label="Add a random draw" disabled={!!props.event}/>
                  {isLottery &&
                    <div>
                      <p>When should we close random draw registration?</p>
                      <DatePick label="Random Draw Deadline" onChange={setLotteryEndDate} value={lotteryEndDate} disabled={!!props.event}/>
                      <p>When will the random draw be run?  <b>This will only be for display purposes.  The random draw will be run manually by the club. </b></p>
                      <DatePick label="Random Draw Run Date" onChange={setLotteryRunDate} value={lotteryRunDate} disabled={!!props.event}/>
                    </div>
                  }
                </div>
              </>
            ) : <></>}
          </Step>
          <Step>
            <div className="stepTitle">Secretary</div>
            <p>Who is the secretary for your event?</p>
            <div className="secretaryModeToggle">
              <button
                type="button"
                className={secretaryMode === "manual" ? "active" : ""}
                onClick={() => {
                  setSecretaryMode("manual");
                  setSelectedTrustedSecretary(null);
                  setTrustedSecretarySearch("");
                  setTrustedSecretaryResults([]);
                }}
              >
                Enter Manually
              </button>
              <button
                type="button"
                className={secretaryMode === "trusted" ? "active" : ""}
                onClick={() => {
                  setSecretaryMode("trusted");
                }}
              >
                Select Trusted Secretary
              </button>
            </div>

            {secretaryMode === "trusted" ? (
              <div className="trustedSecretarySearch">
                <Input
                  value={trustedSecretarySearch}
                  label="Search Trusted Secretary"
                  id="trustedSecretarySearch"
                  type="text"
                  onChange={(e) => {
                    e.stopPropagation();
                    setTrustedSecretarySearch(e.target.value);
                    if (selectedTrustedSecretary && selectedTrustedSecretary.email !== e.target.value) {
                      setSelectedTrustedSecretary(null);
                    }
                  }}
                  placeholder="Search by name or email"
                />
                {isSearchingTrustedSecretary && (
                  <div className="trustedSecretaryHint">Searching trusted secretaries...</div>
                )}
                {!isSearchingTrustedSecretary &&
                  trustedSecretarySearch.trim().length > 0 &&
                  trustedSecretaryResults.length === 0 && (
                    <div className="trustedSecretaryHint">No trusted secretaries found.</div>
                  )}
                {!isSearchingTrustedSecretary && trustedSecretaryResults.length > 0 && (
                  <ul className="trustedSecretaryResults">
                    {trustedSecretaryResults.map((trustedUser) => (
                      <li
                        key={trustedUser._id}
                        onClick={() => {
                          setSelectedTrustedSecretary(trustedUser);
                          setTrustedSecretarySearch(trustedUser.email || trustedUser.display_name);
                          setTrustedSecretaryResults([]);
                          applyTrustedSecretary(trustedUser);
                        }}
                      >
                        <span className="name">
                          {(trustedUser.first_name || "") + " " + (trustedUser.last_name || "")}
                        </span>
                        <span className="email">{trustedUser.email || ""}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : <></>}
            <Input value={secFirst} required label="First Name" id="secFirst" type="text" onChange={(e) => { e.stopPropagation(); setSecFirst(e.target.value); }} placeholder="What's the secretary's first name?" />
            <Input value={secLast} required label="Last Name" id="secLast" type="text" onChange={(e) => { e.stopPropagation(); setSecLast(e.target.value); }} placeholder="What's the secretary's last name?" />
            <Phone label="Phone Number" value={secPhone} onChange={setSecPhone} />
            <Input value={secEmail} required label="Email" id="secEmail" type="email" onChange={(e) => { e.stopPropagation(); setSecEmail(e.target.value); }} placeholder="What's your secretary's email?" />
            {helperService.canEditAddressForContacts(sanctionClub.value) ? (
              <>
                <p>Where is your secretary located?</p>
                <div className="half">
                  <Input label="Address Line 1 or Name of Location" id="secretaryAddress" value={secretaryAddress} type="text" onChange={(e) => { e.stopPropagation(); setSecretaryAddress(e.target.value); }} placeholder="What's secretary's address?" />
                  <Input label="Address Line 2" id="secretaryAddress2" type="text" value={secretaryAddress2} onChange={(e) => { e.stopPropagation(); setSecretaryAddress2(e.target.value); }} placeholder=""/>
                </div>
                <div className="half">
                  <Input label="City" id="secretaryCity" value={secretaryCity} type="text" onChange={(e) => { e.stopPropagation(); setSecretaryCity(e.target.value); }} />
                  <Input label="Postal/ZIP Code" id="secretaryZip" value={secretaryZip} type="text" onChange={(e) => { e.stopPropagation(); setSecretaryZip(e.target.value); }} />
                </div>
                <div className="half">
                  <Dropdown value={secretaryCountry} onChange={(e: any, value: IOption) => { setSecretaryCountry(value); }} icon={flagIcon} label="Country" options={[ { value: "Canada", id: "Canada" }, { value: "USA", id: "USA" } ]} placeholder="Choose secretary's country of residence" />
                  <>
                    {secretaryCountry.id === "USA" && (<Dropdown value={secretaryState} onChange={(e: any, value: IOption) => { setSecretaryState(value); }} icon={pinIcon} label="State" options={States} placeholder="Choose secretary's state" />)}
                  </>
                  <>
                    {secretaryCountry.id === "Canada" && (<Dropdown value={secretaryProvince} onChange={(e: any, value: IOption) => { setSecretaryProvince(value); }} icon={pinIcon} label="Province / State" options={Provinces} alignRight={true} placeholder="Choose secretary's province" />)}
                  </>
                </div>
              </>
            ) : <></>}
          </Step>
          <Step>
            <div className="stepTitle">Chairperson</div>
            <p>Who is the chairperson for your event?</p>
            <Input defaultValue={chairFirst} required label="First Name" id="chairFirst" type="text" onChange={(e) => { e.stopPropagation(); setChairFirst(e.target.value); }} placeholder="What's the chairperson's first name?" />
            <Input defaultValue={chairLast} required label="Last Name" id="chairLast" type="text" onChange={(e) => { e.stopPropagation(); setChairLast(e.target.value); }} placeholder="What's the chairperson's last name?" />
            <Phone label="Phone Number" value={chairPhone} onChange={setChairPhone} />
            <Input defaultValue={chairEmail} required label="Email" id="chairEmail" type="email" onChange={(e) => { e.stopPropagation(); setChairEmail(e.target.value); }} placeholder="What's your chairperson's email?" />
            {helperService.canEditAddressForContacts(sanctionClub.value) ? (
              <>
                <p>Where is your chairperson located?</p>
                <div className="half">
                  <Input label="Address Line 1 or Name of Location" id="chairpersonAddress" defaultValue={chairpersonAddress} type="text" onChange={(e) => { e.stopPropagation(); setChairpersonAddress(e.target.value); }} placeholder="What's chairperson's address?" />
                  <Input label="Address Line 2" id="chairpersonAddress" type="text" defaultValue={chairpersonAddress2} onChange={(e) => { e.stopPropagation(); setChairpersonAddress2(e.target.value); }} placeholder=""/>
                </div>
                <div className="half">
                  <Input label="City" id="chairpersonCity" defaultValue={chairpersonCity} type="text" onChange={(e) => { e.stopPropagation(); setChairpersonCity(e.target.value); }} />
                  <Input label="Postal/ZIP Code" id="chairpersonZip" defaultValue={chairpersonZip} type="text" onChange={(e) => { e.stopPropagation(); setChairpersonZip(e.target.value); }} />
                </div>
                <div className="half">
                  <Dropdown value={chairpersonCountry} onChange={(e: any, value: IOption) => { setChairpersonCountry(value); }} icon={flagIcon} label="Country" options={[ { value: "Canada", id: "Canada" }, { value: "USA", id: "USA" } ]} placeholder="Choose chairperson's country of residence" />
                  <>
                    {chairpersonCountry.id === "USA" && (<Dropdown value={chairpersonState} onChange={(e: any, value: IOption) => { setChairpersonState(value); }} icon={pinIcon} label="State" options={States} placeholder="Choose chairperson's state" />)}
                  </>
                  <>
                    {chairpersonCountry.id === "Canada" && (<Dropdown value={chairpersonProvince} onChange={(e: any, value: IOption) => { setChairpersonProvince(value); }} icon={pinIcon} label="Province / State" options={Provinces} alignRight={true} placeholder="Choose chairperson's province" />)}
                  </>
                </div>
              </>
            ) : <></>}
          </Step>
          <Step>
            <div className="stepTitle">Registration Message</div>
            <p>Configure a custom message to include in registration emails.</p>
            <Editor 
              apiKey={TINYMCE_API_KEY}
              onInit={(evt, editor) => editorRef.current = editor}
              init={{
                plugins: 'emoticons link lists media searchreplace table visualblocks wordcount paste contextmenu',
                toolbar: 'undo redo | fontfamily fontsize | bold italic underline strikethrough | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
                paste_data_images: false,
                paste_enable_default_filters: true,
                paste_auto_cleanup_on_paste: true,
                paste_remove_styles: false,
                paste_remove_styles_if_webkit: false,
                contextmenu: 'cut copy paste | link table',
                tinycomments_mode: 'embedded',
                tinycomments_author: 'Author name',
                invalid_elements : "script",
                menubar: false,
                branding: false,
                mergetags_list: [
                  { value: 'First.Name', title: 'First Name' },
                  { value: 'Email', title: 'Email' },
                ],
                setup: TINYMCE_SETUP,
              }}
              initialValue={registrationMessage}
              />
          </Step>
          <Step>
            <div className="stepTitle">Custom Event Waiver</div>
            <p>Configure a custom waiver for your event or venue.  This is in addition to the sanctioning club (UKC, AKC, etc.) waiver. 
            <b>  Users will be required to accept the waiver before registering.</b></p>
            <div className="waiverEditor">
            <Editor 
              apiKey={TINYMCE_API_KEY}
              onInit={(evt, editor1) => eventWaiverEditorRef.current = editor1}
              init={{
                plugins: 'emoticons link lists media searchreplace table visualblocks wordcount paste contextmenu',
                toolbar: 'undo redo | fontfamily fontsize | bold italic underline strikethrough | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | removeformat',
                paste_data_images: false,
                paste_enable_default_filters: true,
                paste_auto_cleanup_on_paste: true,
                paste_remove_styles: false,
                paste_remove_styles_if_webkit: false,
                contextmenu: 'cut copy paste | link table',
                tinycomments_mode: 'embedded',
                tinycomments_author: 'Author name',
                invalid_elements : "script",
                allow_conditional_comments: false,
                menubar: false,
                branding: false,
                mergetags_list: [
                  { value: 'First.Name', title: 'First Name' },
                  { value: 'Email', title: 'Email' },
                ],
                setup: TINYMCE_SETUP,
              }}
              initialValue={waiver}
              />
            </div>
          </Step>
          <Step>
            <div className="stepTitle">Event Visibility</div>
            <p>Control when your event becomes visible to the public and when entries open.</p>
            {props?.event ? (
              <>
                <p>Event Status</p>
                <Dropdown 
                  value={eventVisibility} 
                  onChange={(e: any, value: IOption) => { setEventVisibility(value); }} 
                  icon={date} 
                  label="Visibility" 
                  options={[ 
                    { value: "Draft", id: "Draft" }, 
                    { value: "Published", id: "Published" } 
                  ]} 
                  placeholder="Choose event visibility" 
                />
                <p style={{marginTop: '10px', fontSize: '14px', color: '#666'}}>
                  <strong style={{fontWeight: '700'}}>Draft:</strong> Event is not visible to others - only you can see it.<br/>
                  <strong style={{fontWeight: '700'}}>Published:</strong> Event is visible to others on your club page and in search results.
                </p>
                {eventVisibility.value === "Published" && (
                  <>
                    <p style={{marginTop: '10px'}}>When do you want entries to open to the public?</p>
                    <Dropdown value={publishOption} onChange={(e: any, value: IOption) => { setPublishOption(value); }} icon={date} label="Entry Open Date" options={[ { value: "Immediately", id: "Immediately" }, { value: "On a Specific Date", id: "On a Specific Date" } ]} placeholder="Choose a publishing option" />
                    <>
                      {publishOption.value === "On a Specific Date" && (<DatePick showTimeSelect={true} label="Publish Date" onChange={setPublishDate} value={publishDate} rightText={timezone.value || Intl.DateTimeFormat().resolvedOptions().timeZone}/>)}
                    </>
                  </>
                )}
              </>
            ) : (
              <p style={{color: '#666'}}>You can set the visibility status after creating your event.</p>
            )}
          </Step>
          <Step onStep={() => {
            createStripeLink();
          }}>
            <div className="stepTitle">Stripe Account</div>
            <p>
              Eager Dog uses Stripe to process transactions, complete your Stripe
              account details so you're able to receive online event
              registrations. <span>This only needs to be done once!</span>{" "}
              Revenue from your events will be automatically deposited into your
              Kennel Club's bank account.
            </p>
            <>
              {(accountStatus?.onboarding_status === "past_due" ||
                accountStatus?.onboarding_status === "currently_due") && (
                <div className={"stripeError " + accountStatus?.onboarding_status}>
                  <div className="iconWrap">
                    <div className="icon alert">
                      <span></span>
                    </div>
                  </div>
                  <div className="message">
                    Your Stripe account is missing important details, you won't
                    be able to create an event until Stripe's requirements are
                    met.
                  </div>
                </div>
              )}
            </>
            <>
              {accountStatus?.onboarding_status === "eventually_due" && (
                <div className={"stripeError " + accountStatus?.onboarding_status}>
                  <div className="iconWrap">
                    <div className="success icon">
                      <span></span>
                    </div>
                  </div>
                  <div className="message">
                    There are some Stripe account details that will eventually
                    need to be provided, but Stripe is happy for now! You're
                    able to create your event and receive online registrations.
                  </div>
                </div>
              )}
            </>
            <>
              {accountStatus?.onboarding_status === "requirements_met" && (
                <div className={"stripeError " + accountStatus?.onboarding_status}>
                  <div className="iconWrap">
                    <div className="success icon">
                      <span></span>
                    </div>
                  </div>
                  <div className="message">
                    Your Stripe account details look good, you're able to create
                    your event and receive registrations.
                  </div>
                </div>
              )}
            </>
            <>
              {stripeLink === "" && (<span className="stripeLink disabled">Add Stripe Account Details</span>)}
            </>
            <>
              {stripeLink !== "" &&
                accountStatus?.onboarding_status !== "requirements_met" && (<Link className="stripeLink" to={stripeLink}>Add Stripe Account Details</Link>
                )}
            </>
          </Step>
        </StepForm>
      </div>
    </div>
  );
};

export default EventForm;
